import './devices.system-administrator.screen.scss';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../i18n/translations';
import {Link, useRouteMatch} from 'react-router-dom';
import {Device} from '../../../../../../backend/interfaces/device';
import {PaginatedTableData} from '../../../../../../interfaces/paginated-table-data';
import {useContext, useEffect} from 'react';
import {DevicesRepository} from '../../../../../../repositories/devices';
import AddIcon from '@mui/icons-material/Add';
import {AuxiliaryDataCacheContext} from '../../../../../../contexts/auxiliary-data-cache/context';
import {AuxiliaryDataCacheContextType} from '../../../../../../contexts/auxiliary-data-cache/types';
import {usePaginatedTable} from '../../../../../../hooks/paginated-table';
import {Box, Button, Stack} from '@mui/material';
import {PaginatedTable} from '../../../../../../components/paginated-table/paginated-table';
import {UserPublicInfo} from '../../../../../../backend/responses/user-public-info';
import {DataCacheManager} from '../../../../../../managers/data-cache';
import {UsersRepository} from '../../../../../../repositories/users';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import SearchInput from '../../../../../../components/search-input/search-input';

// Columns IDs
enum ColumnIdentifier {
  Identifier = 'identifier',
  Type = 'type',
  State = 'state',
  User = 'user',
}

export default function DevicesSystemAdministratorOfficeScreen() {
  const {t} = useTranslation();

  // The `path` lets us build <Route> paths that are
  // relative to the parent route, while the `url` lets
  // us build relative links.
  let {url, path} = useRouteMatch();

  // Get context
  const auxiliaryDataCacheContext: AuxiliaryDataCacheContextType =
    useContext(AuxiliaryDataCacheContext);

  // Fetch devices
  const handleGetDevices = async (limit: number, skip: number, search?: string) => {
    // Load devices
    const result: {totalCount: number; data: Array<Device>} =
      await DevicesRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        undefined,
        undefined,
        false,
        search
      );

    // Process data
    const processedData: PaginatedTableData<Device> = result.data.map((obj) => {
      // Set identifier
      // Note: The serial number is always the identification of any type of device.
      // However, some legacy devices may not have a serial number at the moment, so
      // we need to show the mac address in those cases.
      const identifier: string = obj.serialNumber || obj.macAddress || '';

      // Get the user info
      const userInfo = obj.userId ? getUserInfo(obj.userId) : undefined;

      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        toLink:
          obj.userSignInType === 'code'
            ? ''
            : url.charAt(url.length - 1) === '/'
              ? `${url}${obj.id}`
              : `${url}/${obj.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Identifier,
            value: identifier,
          },
          {
            columnId: ColumnIdentifier.User,
            value: userInfo?.name || undefined,
            avatarUrl: userInfo?.picture || undefined,
          },
          {
            columnId: ColumnIdentifier.State,
            value: obj.isActive
              ? t(translations.devices.root.state_value_true)
              : t(translations.devices.root.state_value_false),
          },
          {
            columnId: ColumnIdentifier.Type,
            value: t(translations.device_types[obj.type]) || '',
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<Device>({
    fetchData: handleGetDevices,
  });

  // Fetch user info
  const fetchUserInfo = async (userId: string) => {
    let updateContext: boolean = false;

    try {
      // User is being fetched
      DataCacheManager.instance.addPendingUser(userId);

      // Fetch user info
      const user: UserPublicInfo = await UsersRepository.instance.getInfoById(userId);

      if (user) {
        // Save it on cache
        DataCacheManager.instance.addUser(userId, user);

        // Update
        updateContext = true;
      } else {
        // Error
        throw new Error('Empty user.');
      }
    } catch (error) {
      // Log
      console.debug(`🔴 Could not get user ${userId}.`, error);

      // Save it on cache to not fetch again
      DataCacheManager.instance.addUserWithError(userId);
    } finally {
      // User was fetched
      DataCacheManager.instance.removePendingUser(userId);

      // Update context with users
      if (updateContext) {
        auxiliaryDataCacheContext.setUsers(DataCacheManager.instance.users);
      }
    }
  };

  // Get user info
  const getUserInfo = (userId: string): UserPublicInfo | undefined => {
    if (DataCacheManager.instance.users[userId]) {
      // Return user info
      return DataCacheManager.instance.users[userId];
    } else {
      if (!DataCacheManager.instance.usersWithError[userId]) {
        if (!DataCacheManager.instance.pendingUsers[userId]) {
          // Fetch user info but don't wait for it
          fetchUserInfo(userId);
        } else {
          // Log
          console.debug("⌛ User already being fetched. Let's wait for id:", userId);
        }
      } else {
        // Log
        console.debug(
          '🔴 Could not get the user because an error occurred previously with id:',
          userId
        );
      }
    }
  };

  // Update the data when the users in the auxiliary cache were updated
  useEffect(() => {
    for (let i = 0; i < data.length; i++) {
      if (
        data[i].item.userId &&
        !data[i].columns.find((col) => col.columnId === ColumnIdentifier.User)?.value
      ) {
        const userInfo = getUserInfo(data[i].item.userId as string);

        if (userInfo) {
          handleUpdateItem(data[i].id, [
            {
              updatedColumnId: ColumnIdentifier.User,
              updatedColumnValue: userInfo.name,
              updatedColumnAvatarUrl: userInfo.picture,
            },
          ]);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auxiliaryDataCacheContext.state]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        pageTitle={t(translations.screen_paths.senior_devices)}
        isLoading={false}
        leftColumn={
          <Stack
            gap={2}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              paddingTop: '10px',
            }}
          >
            <SearchInput
              placeholder={t(translations.devices.root.search_input_placeholder)}
              value={currentSearch}
              onChange={(event) => handleUpdateCurrentSearch(event.target.value)}
              onSearchAction={(value: string) => handleApplyFilters(1, value)}
            />
          </Stack>
        }
        rightColumn={
          <>
            <Link className="devices-system-administrator-screen-button-link" to={`${path}/create`}>
              <Button variant="inverted" color="primary" endIcon={<AddIcon />}>
                {t(translations.devices.root.add_button)}
              </Button>
            </Link>
          </>
        }
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.devices.root.no_devices)}
            columns={[
              {
                columnId: ColumnIdentifier.Identifier,
                title: t(translations.devices.root.identifier_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.Type,
                title: t(translations.devices.root.type_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.User,
                title: t(translations.devices.root.user_header),
                flexGrow: 1,
                chip: true,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.devices.root.state_header),
                flexGrow: 1,
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={true}
          />
        </Box>
      </Box>
    </Box>
  );
}
